import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { NotificationListItem } from '@/Containers/CommunicationSection/NotificationContainer/Components/NotificationListItem'
import { getDayLabel } from '@/Containers/CommunicationSection/NotificationContainer/Helpers/getDayLabel'
import { notificationManager } from '@/Containers/CommunicationSection/NotificationContainer/Stores/NotificationManager'
import { ChangeAllNotificationsIsReadedTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/ChangeAllNotificationsIsReadedTask'
import { ChangeNotificationIsReadedTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/ChangeNotificationIsReadedTask'
import { LoadNotificationListTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/LoadNotificationListTask'
import { LoadTabUnreadCountsTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/LoadTabUnreadCountsTask'
import { LoadUnreadCountTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/LoadUnreadCountTask'
import { DataList } from '@/Core/UI/DataList'
import { cx } from '@/Ship/Helpers/cssClass'

import { driverDashboardManager } from '../Stores/DriverDashboardManager'

import styles from './NotificationsPage.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

export const pageConfig: IPageConfig = {
    route: '/driver/notifications',
    key: 'Driver.Notifications',
    title: 'Сповіщення',
    template: 'driver',
}

type Tab = 'tasks' | 'dispatcher'

/** Сповіщення водія: «Завдання» (нові призначення, системні) і «Від диспетчера» */
const NotificationsPage = observer(() => {
    document.title = 'Сповіщення'

    const { notifications, isLoadingFirstPage, isNotificationLoading, unreadTasksCount, unreadDispatcherCount } =
        notificationManager
    // 'tasks' — сповіщення про завдання: нове призначення (type='task') та системні (type='system')
    // 'dispatcher' — повідомлення від диспетчера (type='dispatcher')
    const [activeTab, setActiveTab] = useState<Tab>('tasks')
    const lockRef = useRef(false)
    const navigate = useNavigate()

    const preparedList = useMemo(() => {
        const result: any[] = []
        let lastLabel: string | null = null

        notifications.forEach((item) => {
            const label = getDayLabel(item.createdAt)
            if (label !== lastLabel) {
                // _row marks the row kind; the notification's own `type`
                // field must not be overwritten by the list marker.
                result.push({ _row: 'divider', label, key: `divider-${label}` })
                lastLabel = label
            }
            result.push({ _row: 'item', ...item })
        })

        return result
    }, [notifications])

    const loadFirstPage = useCallback((extraParams = {}) => {
        lockRef.current = false
        notificationManager.setPagination({ page: 1, total: 0 })
        notificationManager.setNotifications([])
        LoadNotificationListTask({ searchParams: { page: 1, ...extraParams } })
    }, [])

    const paramsForTab = (tab: Tab) => (tab === 'dispatcher' ? { type: 'dispatcher' } : { type: ['system', 'task'] })

    const switchTab = (next: Tab) => {
        if (next === activeTab) return
        setActiveTab(next)
        loadFirstPage(paramsForTab(next))
    }

    useEffect(() => {
        loadFirstPage(paramsForTab(activeTab))
        // Лічильник на бейджі навігації й лічильники вкладок — при заході на сторінку оновлюємо
        LoadUnreadCountTask()
        LoadTabUnreadCountsTask()
        return () => {
            notificationManager.setNotifications([])
            notificationManager.setPagination({ page: 1, total: 0 })
            // Скидаємо scope на завдання, щоб наступний вхід через нижню
            // навігацію показував усі сповіщення, а не лише останнього завдання.
            notificationManager.setEntityFilter(null)
        }
    }, [])

    const tabs: Array<{ key: Tab; label: string; count: number }> = [
        { key: 'tasks', label: 'Завдання', count: unreadTasksCount },
        { key: 'dispatcher', label: 'Від диспетчера', count: unreadDispatcherCount },
    ]

    return (
        <div className="pwa-page">
            <h1 className="pwa-h1">Сповіщення</h1>

            <div className={cx(styles, 'tabs')} role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`${cx(styles, 'tab')}${activeTab === tab.key ? ` ${cx(styles, 'tab--active')}` : ''}`}
                        onClick={() => switchTab(tab.key)}
                    >
                        {tab.label}
                        {tab.count > 0 && (
                            <span className={cx(styles, 'count')}>{tab.count > 99 ? '99+' : tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            <div className={`pwa-card ${cx(styles, 'list')}`}>
                <DataList
                    itemLayout="vertical"
                    loading={isLoadingFirstPage}
                    dataSource={preparedList}
                    locale={{ emptyText: 'Сповіщень поки немає' }}
                    renderItem={(item) => {
                        if (item._row === 'divider') {
                            return (
                                <div key={item.key} className={cx(styles, 'day')}>
                                    {item.label}
                                </div>
                            )
                        }

                        return (
                            <NotificationListItem
                                item={item}
                                onClick={async () => {
                                    if (!item.isReaded) {
                                        await ChangeNotificationIsReadedTask({ uuid: item.uuid })
                                        LoadTabUnreadCountsTask()
                                    }
                                    // Сповіщення про завдання посилається на ExecutorAssignment
                                    // (entityCode='Assignment', entityUuid=uuid завдання): ведемо
                                    // водія на головну й відкриваємо деталі цього завдання.
                                    if (item.entityCode === 'Assignment' && item.entityUuid) {
                                        driverDashboardManager.requestOpenByUuid(item.entityUuid)
                                        navigate('/driver')
                                    }
                                    // Нагадування завершити зміну (entityCode='Shift'): зміна й кнопка
                                    // «Завершити» — на головній кабінету
                                    if (item.entityCode === 'Shift') {
                                        navigate('/driver')
                                    }
                                }}
                            />
                        )
                    }}
                />

                {isNotificationLoading && <div className={cx(styles, 'loading')}>Завантаження…</div>}

                {notifications.length > 0 && (
                    <div className={cx(styles, 'footer')}>
                        <button
                            type="button"
                            className="pwa-btn pwa-btn--ghost pwa-btn--block pwa-btn--compact"
                            onClick={async () => {
                                await ChangeAllNotificationsIsReadedTask()
                                // Перечитуємо активну вкладку й лічильники непрочитаних
                                loadFirstPage(paramsForTab(activeTab))
                                LoadTabUnreadCountsTask()
                            }}
                        >
                            Позначити всі як прочитані
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
})

export default NotificationsPage
